<template>
  <div>
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        mdi-bus-multiple
      </v-icon>
        <span class="px-2">{{ $t('buses') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="showBusDialog"></create-button>
        <activation-tool-tip model="buses"></activation-tool-tip>
      </v-card-title>
      <v-data-table
        v-model="selected"
        show-select
        :options.sync="tableOptions"
        :footer-props="footerProps"
        :no-data-text="$t('noData')"
        :no-results-text="$t('noResults')"
        item-key="id"
        :loading="isLoading"
        loading-text="Loading... Please wait"
        :headers="translatedHeaders"
        :items="buses"
        :search="debouncedSearch"
      >
        <template v-slot:top>
          <bulk-action-bar
            :count="selected.length"
            :running="bulkRunning"
            :done="bulkDone"
            :total="bulkTotal"
            :progress="bulkProgress"
            @clear="clearSelection"
          >
            <v-btn text small @click="bulkDelete">
              <v-icon small class="me-1">mdi-delete</v-icon>
              {{ $t('delete_selected') }}
            </v-btn>
          </bulk-action-bar>
          <v-text-field
            v-model="search"
            :label="$t('search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.driver="{ item }">
          <v-chip :color="getDriverAssignmentColor(item.driver)" dark @click="assignDriver(item)">
            {{ getDriver(item.driver) }}
          </v-chip>
        </template>
        <!-- how full the bus is: the seats were listed, the children in them
             were not, so nobody could tell a full bus from an empty one -->
        <template v-slot:item.students="{ item }">
          <v-chip
            small
            dark
            :color="seatColor(item)"
            @click="showStudents(item)"
          >
            {{ studentCount(item) }} / {{ item.capacity }}
          </v-chip>
        </template>
        <!-- a bus with a driver but no trip carries nobody, which the list gave
             no way of telling apart from one that is fully in service -->
        <template v-slot:item.trips="{ item }">
          <v-chip small dark :color="(item.trips || []).length ? 'success' : 'warning'">
            {{ (item.trips || []).length || $t('no_trips') }}
          </v-chip>
        </template>
        <template v-slot:item.routes="{ item }">
          <span v-if="routesFor(item).length">
            <v-chip
              v-for="route in routesFor(item)"
              :key="route.id"
              class="ma-1"
              x-small
              dark
              :color="route.is_morning ? 'primary' : 'secondary'"
              @click="viewRoute(route)"
            >
              {{ route.name }}
            </v-chip>
          </span>
          <span v-else>-</span>
        </template>
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon v-if="item.driver" small class="mx-2" @click="viewDriver(item.driver)">
            mdi-eye
          </v-icon>
          <v-icon v-if="item.driver" small class="mx-2" @click="unAssignDriver(item)">
            mdi-account-off
          </v-icon>
          <v-icon v-else small class="mx-2" @click="assignDriver(item)">
            mdi-account-tie-hat
          </v-icon>
          <v-icon small class="mx-2" @click="editBus(item)">
            mdi-pencil
          </v-icon>
          <v-icon small class="mx-2" @click="deleteBus(item)">
            mdi-delete
          </v-icon>
        </template>
        <!-- a school with no buses met "No data available" and no idea what
             to do about it -->
        <template v-slot:no-data>
          <empty-state
            icon="mdi-bus-multiple"
            :title="$t('no_buses_yet')"
            :body="$t('no_buses_yet_hint')"
            :action="$t('add_a_bus')"
            :secondary="$t('import_from_a_spreadsheet')"
            :secondary-to="{ name: 'imports', query: { kind: 'buses' } }"
            @action="showBusDialog"
          ></empty-state>
        </template>
      </v-data-table>
    </v-card>
    <v-row justify="center">
      <v-dialog
        v-model="busDialog"
        persistent
        max-width="600px"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ $t('bus_data') }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="license"
                      :rules="licenseRules"
                      :label="$t('license_plate') + '*'"
                      :hint="$t('license_plate_of_bus')"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-text-field
                      v-model="capacity"
                      :rules="capacityRules"
                      :label="$t('capacity') + '*'"
                      :hint="$t('number_of_seats')"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                :disabled="isSaving"
                @click="busDialog = false"
              >
                {{ $t('close') }}
              </v-btn>
              <!-- keeps the dialog open so a run of buses can be typed in one go -->
              <v-btn
                v-if="!id"
                color="blue darken-1"
                text
                :loading="isSaving"
                @click="createBus(true)"
              >
                {{ $t('save_and_add_another') }}
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                :loading="isSaving"
                @click="createBus(false)"
              >
                {{ $t('save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>
    </v-row>
    <v-dialog v-if="selectedBus" v-model="driversDialog" max-width="390">
      <v-card>
        <v-card-title class="text-h5">{{ $t('select_driver_for_bus', { license: selectedBus.license }) }}</v-card-title>

        <v-card-text>
          <v-list dense>
            <v-subheader>{{ $t('drivers') }}</v-subheader>
            <v-list-item-group>
              <v-list-item
                v-for="(driver, i) in availableDrivers"
                :key="i"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="driver.name" @click="assignDriverToBus(driver)"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-container style="height: 400px">
          <v-row
            v-show="loadingDrivers"
            class="fill-height"
            align-content="center"
            justify="center"
          >
            <v-col class="text-subtitle-1 text-center" cols="12">
              {{ $t('please_wait') }}
            </v-col>
            <v-col cols="6">
              <v-progress-linear
                :active="loadingDrivers"
                color="primary"
                indeterminate
                rounded
                height="6"
              ></v-progress-linear>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="green darken-1"
            text
            @click="closeDriverDialog"
          >
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <bulk-result-dialog
      v-model="bulkResultDialog"
      :title="bulkResultTitle"
      :failures="bulkFailures"
    ></bulk-result-dialog>
    <undo-bar
      :pending="pendingUndo"
      :seconds-left="undoSecondsLeft"
      @undo="undoPendingRemove"
    ></undo-bar>
    <students-on-dialog
      v-model="studentsDialog"
      scope="bus"
      :subject="studentsSubject"
    ></students-on-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import CreateButton from "@/components/CreateButton";
import UndoBar from "@/components/UndoBar";
import undoable from "@/mixins/undoable";
import tableState from "@/mixins/tableState";
import bulkActions from "@/mixins/bulkActions";
import BulkActionBar from "@/components/BulkActionBar";
import BulkResultDialog from "@/components/BulkResultDialog";
import StudentsOnDialog from "@/components/StudentsOnDialog";
import EmptyState from "@/components/EmptyState";

export default {
  mixins: [undoable, tableState, bulkActions],
  components: {
    EmptyState,
    ActivationToolTip,
    CreateButton,
    UndoBar,
    BulkActionBar,
    BulkResultDialog,
    StudentsOnDialog,
  },
  data() {
    return {
      buses: [],
      studentCounts: {},
      studentsDialog: false,
      studentsSubject: null,
      availableDrivers: [],
      isLoading: false,
      isSaving: false,
      tableStateKey: "buses",
      busDialog: false,
      driversDialog: false,
      loadingDrivers: false,
      valid: true,
      id: null,
      selectedBus: null,
      license: '',
      licenseRules: [
        v => !!v || this.$t('license_plate_required'),
        v => (v && v.length <= 15) || this.$t('license_plate_max_length'),
      ],
      capacity: '',
      capacityRules: [
        v => /^[0-9]+$/.test(v) || this.$t('capacity_required'),
      ],
      headers: [
        { text: "ID", value: "id", align: "start", filterable: false },
        { text: "License", value: "license" },
        { text: "Capacity", value: "capacity" },
        { text: "Driver", value: "driver" },
        { text: "students", value: "students", sortable: false },
        { text: "trips", value: "trips", sortable: false },
        { text: "routes", value: "routes", sortable: false },
        { text: "Created", value: "created_at" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  mounted() {
    this.loadBuses();
    this.loadStudentCounts();
  },
  computed: {
    translatedHeaders() {
      return this.headers.map(header => ({
        ...header,
        text: this.$t(header.text.toLowerCase().replace(' ', '_'))
      }));
    },
  },
  methods: {
    //one request for the whole table rather than one per row
    loadStudentCounts() {
      axios
        .get("/school-students/assignments")
        .then((response) => {
          this.studentCounts = (response.data && response.data.buses) || {};
        })
        .catch(() => {
          //the rest of the table is still worth showing
        });
    },
    studentCount(bus) {
      return this.studentCounts[bus.id] || 0;
    },
    //amber once it is nearly full, red once the mapping has overbooked it
    seatColor(bus) {
      const count = this.studentCount(bus);
      const capacity = parseInt(bus.capacity, 10) || 0;
      if (!count) return "warning";
      if (capacity && count > capacity) return "error";
      if (capacity && count >= capacity * 0.9) return "orange";
      return "primary";
    },
    showStudents(bus) {
      this.studentsSubject = bus;
      this.studentsDialog = true;
    },
    //the distinct routes this bus's trips run, in the order they come back
    routesFor(bus) {
      const routes = [];
      (bus.trips || []).forEach((trip) => {
        if (trip.route && !routes.some((route) => route.id === trip.route.id)) {
          routes.push(trip.route);
        }
      });
      return routes;
    },
    viewDriver(driver) {
      this.$router.push({ name: "view-driver", params: { user_id: driver.id } });
    },
    viewRoute(route) {
      this.$router.push({ name: "view-route", params: { route_id: route.id } });
    },
    loadBuses() {
      this.isLoading = true;
      this.buses = [];
      axios
        .get(`/buses/all`)
        .then((response) => {
          this.buses = response.data;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_buses"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate () {
      return this.$refs.form.validate()
    },
    createBus(addAnother = false) {
      if(this.validate())
      {
        const editingId = this.id;
        this.isSaving = true;
        axios
          .post(`/buses/create-edit`, {
            bus: {
              id: editingId,
              license: this.license,
              capacity: this.capacity,
            },
          })
          .then(() => {
            this.applySavedBus(editingId);
            this.$notifySuccess(editingId ? this.$t("bus_updated") : this.$t("bus_created"));

            if (addAnother) {
              this.resetBusForm();
            } else {
              this.busDialog = false;
            }
          })
          .catch((error) => {
            this.$notifyError(error, this.$t("error_creating_bus"));
          })
          .then(() => {
            this.isSaving = false;
          });
      }
    },
    // /buses/create-edit answers with a message only, never the saved row.
    // On an edit we already hold every field that changed, so patch the row from
    // the form and keep the admin's search, page and scroll. A create still needs
    // one reload, because only the server knows the new id.
    applySavedBus(editingId) {
      if (!editingId) {
        this.loadBuses();
        return;
      }

      const index = this.buses.findIndex((bus) => bus.id === editingId);
      if (index === -1) {
        this.loadBuses();
        return;
      }

      this.$set(this.buses, index, {
        ...this.buses[index],
        license: this.license,
        capacity: this.capacity,
      });
    },
    resetBusForm() {
      this.license = '';
      this.capacity = '';
      this.id = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    showBusDialog() {
      this.license = '';
      this.capacity = '';
      this.id = null;
      this.busDialog = true;
    },
    editBus(bus) {
      this.id = bus.id;
      this.license = bus.license;
      this.capacity = bus.capacity;
      this.busDialog = true;
    },
    bulkDelete() {
      this.runBulk({
        items: this.selected,
        title: this.$t("delete_selected"),
        confirmText: this.$t("confirm_bulk_delete", { count: this.selected.length }),
        label: (bus) => bus.license,
        request: (bus) => axios.delete(`/buses/${bus.id}`),
        onSuccess: (bus) => this.removeFromList(this.buses, bus),
      });
    },
    //one click: the row goes now, the request goes in five seconds unless undone
    deleteBus(bus) {
      this.undoableRemove({
        item: bus,
        list: this.buses,
        label: bus.license,
        request: () => axios.delete(`/buses/${bus.id}`),
        onDone: () => this.$notifySuccess(this.$t("bus_deleted")),
        onError: (error) => this.$notifyError(error, this.$t("error_deleting_bus")),
      });
    },
    getDriverAssignmentColor(driver) {
      if (driver) return "success";
      else return "error";
    },
    getDriver(driver) {
      if (driver) return driver.name;
      else return this.$t("none");
    },
    assignDriver(item) {
      this.selectedBus = item;
      this.driversDialog = true;
      this.loadAvailableDrivers()
    },
    loadAvailableDrivers() {
      this.loadingDrivers = true;
      this.availableDrivers = [];
      axios
        .get('/buses/available-drivers')
        .then((response) => {
          this.availableDrivers = response.data;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_drivers"));
        })
        .then(() => {
          this.loadingDrivers = false;
        });
    },
    assignDriverToBus(driver) {
      this.loadingDrivers = true;
      const bus = this.selectedBus;
      axios
        .post(`/buses/assign-driver`, {
          bus_id: bus.id,
          driver_id: driver.id,
        })
        .then(() => {
          //we know which driver went onto which bus, so patch the row directly
          this.setBusDriver(bus.id, driver);
          this.$notifySuccess(this.$t("driver_assigned_to_bus"));
          this.closeDriverDialog();
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_assigning_driver"));
        })
        .then(() => {
          this.loadingDrivers = false;
        });
    },
    setBusDriver(bus_id, driver) {
      const index = this.buses.findIndex((bus) => bus.id === bus_id);
      if (index === -1) {
        this.loadBuses();
        return;
      }
      this.$set(this.buses, index, { ...this.buses[index], driver: driver });
    },
    //unassigning is trivially reversible from this same screen, so it does not
    //need a confirmation step of its own
    unAssignDriver(item) {
      this.unAssignDriverFromBus(item);
    },
    unAssignDriverFromBus(item) {
      this.isLoading = true;
      axios
        .post(`/buses/unassign-driver`, {
          bus_id: item.id,
        })
        .then(() => {
          this.setBusDriver(item.id, null);
          this.$notifySuccess(this.$t("driver_unassigned_from_bus"));
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_unassigning_driver"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    closeDriverDialog() {
      this.driversDialog = false;
      this.loadingDrivers = false;
      this.availableDrivers = [];
    },
  },
};
</script>
